import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertCircle } from "lucide-react";

/**
 * Sitewide banner sourced from `/api/runtime-flags`.
 *
 * Shows ONE banner at a time — `maintenance_mode` wins over
 * `feature_outage_notice` because maintenance is the more severe
 * state. When neither is set, renders nothing.
 *
 * Polls every 60s so admins toggling the flag in the Operations
 * Console see the banner appear/disappear across all open tabs
 * without forcing a refresh. Unauthenticated visitors also see
 * maintenance banners since the endpoint is public — important for
 * the marketing site.
 */
export default function RuntimeBanner() {
  const [flags, setFlags] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/runtime-flags");
        if (!cancelled) setFlags(data);
      } catch {
        // swallow — banner is best-effort
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!flags) return null;
  if (flags.maintenance_mode) {
    return (
      <Bar
        tone="amber"
        message="CoParent Console is briefly down for maintenance. Read access still works; new edits are paused. Please try again shortly."
        testid="runtime-banner-maintenance"
      />
    );
  }
  // v77.99.16 — Admin-configurable broadcast. Sits between maintenance
  // (most severe) and feature_outage_notice (less severe) so an admin
  // can override the generic outage copy with a specific message
  // without flipping the whole site into read-only mode.
  if (flags.admin_broadcast && flags.admin_broadcast.enabled && flags.admin_broadcast.message) {
    const tone = flags.admin_broadcast.tone || "info";
    const toneMap = { info: "teal", warning: "amber", urgent: "red" };
    return (
      <Bar
        tone={toneMap[tone] || "teal"}
        message={flags.admin_broadcast.message}
        testid="runtime-banner-broadcast"
      />
    );
  }
  if (flags.feature_outage_notice) {
    return (
      <Bar
        tone="amber"
        message="Some features are temporarily unavailable while we investigate an issue."
        testid="runtime-banner-outage"
      />
    );
  }
  return null;
}

function Bar({ tone, message, testid }) {
  // v77.99.16 — three tones: teal (informational), amber (warning),
  // red (urgent). Existing maintenance + outage banners continue to
  // use amber so we don't visually change the in-flight state today.
  const palettes = {
    teal: "bg-[#E8F5F5] text-[#0F5658] border-[#A7D8DA]",
    amber: "bg-[#FDF4E2] text-[#8F6B1F] border-[#E6CFA2]",
    red: "bg-[#FBEFED] text-[#8C2A22] border-[#E8B8B2]",
  };
  const palette = palettes[tone] || palettes.amber;
  return (
    <div
      className={`border-b ${palette} px-4 py-2 text-[13px] flex items-center gap-2 justify-center`}
      role="status"
      data-testid={testid}
    >
      <AlertCircle className="w-4 h-4 shrink-0" />
      <span className="text-center">{message}</span>
    </div>
  );
}
